import React, { useEffect, useState } from 'react';
import { DATA_CONFIGURATION, DEFAULT_VALUE, SELECT_TYPE } from '../ISRConstants';
import { TConnector, TField, TFieldDataConfiguration, TProductCategory, TSubmodel } from '../Pages/DefineModel/model';
import Card from 'Components/DesignSystem/Card/Card';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';

type BooleanConfigurationProps = {
    id: string;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    updateFieldDataConfiguration: (updatedConfig: TFieldDataConfiguration) => void;
}

/*
    "Select" is a real option carrying the literal STRING "Select" as its value,
    not an empty sentinel — that is exactly what the native `<option>Select</option>`
    did, since an option with no `value` takes its text as its value. Picking it
    CLEARS the field: both handlers store `null`, never the literal "Select"
    (which is what used to land in the payload, and `optionsMap.get` then missed).
    Clearing the type also clears the default, since its options depend on the type.
    `null` matches no option, so the placeholder shows — which is also what the
    native select rendered.
*/
const SELECT_SENTINEL = 'Select';

/**
 * The API takes `selectType` / `defaultValue` as a non-empty string or `null` — NEVER `''`.
 * Empty, whitespace-only, or the "Select" sentinel → `null`; anything else → its string.
 */
export const toApiValue = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    const str = String(value);
    return str.trim() === '' || str === SELECT_SENTINEL ? null : str;
};

const BooleanConfiguration = ({id, selectedItem, updateFieldDataConfiguration}: BooleanConfigurationProps) => {
    const existingValue = selectedItem && 'dataConfiguration' in selectedItem && selectedItem?.dataConfiguration ? selectedItem.dataConfiguration : null;
    /*
        Records saved before 2026-09-24 can carry `''` (a type change used to write
        `defaultValue: ''`) or the literal "Select". Normalise both to `null` on load, and seed
        the keys as explicit `null` on a field that has none. The mount effect below pushes this
        back into the project, so the next autosave repairs a legacy record.
    */
    const [fieldDataConfiguration, setFieldDataConfiguration] = useState<TFieldDataConfiguration>(() =>
        existingValue
            ? { ...existingValue, selectType: toApiValue(existingValue.selectType), defaultValue: toApiValue(existingValue.defaultValue) }
            : {}
    );
    const optionsMap = new Map([
        ['True/False', ['True', 'False']],
        ['Yes/No', ['Yes', 'No']],
        ['0/1', ['0', '1']],
    ]);

    useEffect(() => {
        const hasChanges = Object.keys(fieldDataConfiguration).some(
            (key) => fieldDataConfiguration[key as keyof TFieldDataConfiguration] !== undefined
        );
        if (hasChanges) {
            updateFieldDataConfiguration(fieldDataConfiguration);
        }
    }, [fieldDataConfiguration]);

    const handleTypeChange = (value: string | number) => {
        const selectType = toApiValue(value);
        // The default's options depend on the type, so any type change resets it — to `null`, never `''`.
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, selectType, defaultValue: null}));
    };

    const handleDefaultTypeChange = (value: string | number) => {
        const defaultValue = toApiValue(value);
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, defaultValue}));
    }

    const typeOptions = [
        { value: SELECT_SENTINEL, label: SELECT_SENTINEL },
        ...['True/False', 'Yes/No', '0/1'].map((item) => ({ value: item, label: item })),
    ];

    const defaultValueOptions = [
        { value: SELECT_SENTINEL, label: SELECT_SENTINEL },
        ...(fieldDataConfiguration?.selectType
            ? (optionsMap.get(fieldDataConfiguration.selectType) ?? []).map((item) => ({ value: item, label: item }))
            : []),
    ];

    return (
        <Card id={id} data-testid={id} title={DATA_CONFIGURATION}>
            <div id={`${id}-main-section`} className='tw-flex tw-items-start tw-gap-5'>
                <Dropdown
                    id={`${id}-select-type`}
                    data-testid={`${id}-select-type`}
                    label={SELECT_TYPE}
                    placeholder={SELECT_SENTINEL}
                    options={typeOptions}
                    value={fieldDataConfiguration?.selectType ?? undefined}
                    onChange={handleTypeChange}
                    usePortal
                    className='tw-w-[15%]'
                />
                <Dropdown
                    id={`${id}-default-value-select`}
                    data-testid={`${id}-default-value-select`}
                    label={DEFAULT_VALUE}
                    placeholder={SELECT_SENTINEL}
                    options={defaultValueOptions}
                    value={fieldDataConfiguration?.defaultValue ?? undefined}
                    onChange={handleDefaultTypeChange}
                    usePortal
                    className='tw-w-[15%]'
                />
            </div>
        </Card>
    );
}

export default BooleanConfiguration;
